import { useMsal } from '@azure/msal-react';
import { DropDownList, MultiSelect } from '@progress/kendo-react-dropdowns';
import { Checkbox, Switch } from '@progress/kendo-react-inputs';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useInternalUserCheck from '../../Hooks/Misc/useInternalUserCheck';
import useGetProjects from '../../Hooks/Projects/useGetProjects';
import { isLibraryOrganization } from '../Helpers/Organization/Organization';
import { disableAllInstancesOfApplicationOnRuntime, postMessageToRuntime } from '../Helpers/Runtime/runtime';
import ErrorMessage from '../Shared/Error/ErrorMessage';
import { Upload } from '../Shared/FormFields/Upload';
import IconPicker from '../Shared/IconPicker/IconPicker';
import DeleteProjectDialog from './DeleteProjectDialog';
import ProjectSettingFileList from './ProjectSettingFileList';
import { PageContainer, Header } from "../Drawer/PageContainer";
import Loader from '../Shared/Loader/loader';
import { updateProjectLabelAndUrlOnRuntime, uploadFiles } from './utils';
import { DeleteFile } from '../Helpers/DateLakeStorage';
import Scrollable from '../Shared/Edit/Scrollable';
import RemoveDialog from '../Shared/Remove/RemoveDialog';
import { OverlayLoader } from '../Shared/Loader/OverlayLoader';
import InnerLoader from '../Shared/Loader/InnerLoader';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from '../../Renderers/GlobalContext';
import { updateProject } from '../../API/Project/project';
import useGetProjectTenants from '../../Hooks/Projects/useGetProjectTenants';
import SaveDialog from '../Shared/Remove/SaveDialog';
import { callApiWithAuthorization } from '../../API/CallApi';
import Upgarde from '../Shared/Subscription/Upgrade';

export function ProjectSetting({ project, isAccessedInIframe }) {

    const { organization, user, subscription } = useContext(GlobalContext).state;
    const projectId = project._id;

    const msalContext = useMsal();
    const navigate = useNavigate();
    const isLibrary = isLibraryOrganization(organization.orgName);
    const { t } = useTranslation([
        "project_setting/common"
    ], {
        useSuspense: false
    })
    const isInternalUser = useInternalUserCheck(user.email);
    const [projectsLoading, projects, projectsLoadingError] = useGetProjects(organization._id);
    const [tenantsLoading, tenants, tenantsLoadingError] = useGetProjectTenants(projectId);

    const [saving, setSaving] = useState(false);

    const [defaultProjectDetails, setDefaultProjectDetails] = useState({
        tags: []
    });
    const [projectDetails, setProjectDetails] = useState({
        tags: []
    });
    const [projectDetailsErrors, setProjectDetailErrors] = useState({
        name: "",
        url: ""
    })

    const [defaultLibraryDetails, setDefaultLibraryDetails] = useState({
        tilesImages: [],
        slideshowImages: []
    });
    const [libraryDetails, setLibraryDetails] = useState({
        tilesImages: [],
        slideshowImages: []
    });

    const [showRemoveFileDialog, setShowRemoveFileDialog] = useState(false);
    const [fileToRemove, setFileToRemove] = useState("");

    const [showNetworkError, setShowNetworkError] = useState(false);
    useEffect(() => {
        if (showNetworkError) {
            setTimeout(() => {
                setShowNetworkError(false);
            }, 5000)
        }
    }, [showNetworkError])

    const [developerMode, setDeveloperMode] = useState(false);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [showMasterAppToogleWarningDialog, setShowMasterAppToogleWarningDialog] = useState(false);

    const [showCloneAppDialog, setShowCloneAppDialog] = useState(false);

    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

    const [masterApp, setMasterApp] = useState(false);

    useEffect(() => {
        if (projects && projects.length > 0) {
            const project = projects.find(item => (item._id === projectId));
            const tags = project.tag.length === 0 ? [] : project.tag.split(',');
            const defaultProjectDetails = {
                name: project.name,
                url: project.url,
                description: project.description,
                tags: tags,
                abbreviation: project.abbreviation,
                icon: project.Icon,
                developerMode: project.developerMode === true,
                masterApp: project.masterApp === true,
                allowSyncingSharedProperty: project.allowSyncingSharedProperty === true,
                bgColor: project.bgColor
            }
            setDefaultProjectDetails({ ...defaultProjectDetails });
            setProjectDetails({ ...defaultProjectDetails });
            setDeveloperMode(project.developerMode === true);
            setMasterApp(project.masterApp === true);

            if (isLibrary) {
                const libraryDetails = {
                    isPublished: project.isPublished === true,
                    isSuperApp: project.isSuperApp === true,
                    isFeaturedApp: project.isFeaturedApp === true,
                    hasTableFeature: project.hasTableFeature === true,
                    hasCardsFeature: project.hasCardsFeature === true,
                    hasCalendarFeature: project.hasCalendarFeature === true,
                    hasSplitViewFeature: project.hasSplitViewFeature === true,
                    hasListsFeature: project.hasListsFeature === true,
                    hasChartsFeature: project.hasChartsFeature === true,
                    hasReportingFeature: project.hasReportingFeature === true,
                    hasDashboardFeature: project.hasDashboardFeature === true,
                    existingTileImages: project.TilesImages ? project.TilesImages.split(',') : [],
                    existingSlideshowImages: project.SlideshowImages ? project.SlideshowImages.split(',') : [],
                    masterAppId: project.masterAppId || project.instances[0]._id,
                    tilesImages: [],
                    slideshowImages: []
                }
                setDefaultLibraryDetails({ ...libraryDetails });
                setLibraryDetails({ ...libraryDetails });
            }
        }
    }, [projects])

    const [saveDisabled, setSaveDisabled] = useState(true);

    useEffect(() => {
        const isFormValuesSame = (
            defaultProjectDetails.name === projectDetails.name &&
            defaultProjectDetails.description === projectDetails.description &&
            defaultProjectDetails.abbreviation === projectDetails.abbreviation &&
            defaultProjectDetails.icon === projectDetails.icon &&
            developerMode === defaultProjectDetails.developerMode &&
            masterApp === defaultProjectDetails.masterApp &&
            projectDetails.allowSyncingSharedProperty === defaultProjectDetails.allowSyncingSharedProperty &&
            areTagsSame(defaultProjectDetails.tags, projectDetails.tags)
        );
        const projectDetailsHasErrors = Object.values(projectDetailsErrors).some(value => value);
        if (isLibrary) {
            const isLibraryFormValuesSame = (
                defaultLibraryDetails.isPublished === libraryDetails.isPublished &&
                defaultLibraryDetails.isSuperApp === libraryDetails.isSuperApp &&
                defaultLibraryDetails.isFeaturedApp === libraryDetails.isFeaturedApp &&
                defaultLibraryDetails.hasTableFeature === libraryDetails.hasTableFeature &&
                defaultLibraryDetails.hasCardsFeature === libraryDetails.hasCardsFeature &&
                defaultLibraryDetails.hasCalendarFeature === libraryDetails.hasCalendarFeature &&
                defaultLibraryDetails.hasSplitViewFeature === libraryDetails.hasSplitViewFeature &&
                defaultLibraryDetails.hasListsFeature === libraryDetails.hasListsFeature &&
                defaultLibraryDetails.hasChartsFeature === libraryDetails.hasChartsFeature &&
                defaultLibraryDetails.hasReportingFeature === libraryDetails.hasReportingFeature &&
                defaultLibraryDetails.hasDashboardFeature === libraryDetails.hasDashboardFeature &&
                defaultLibraryDetails.masterAppId === libraryDetails.masterAppId &&
                libraryDetails.tilesImages.length === 0 &&
                libraryDetails.slideshowImages.length === 0
            );
            const tileImageHasError = libraryDetails.tilesImages.some(image => !!image.validationErrors);
            const slideshowImageHasError = libraryDetails.slideshowImages.some(image => !!image.validationErrors);
            const saveDisabled = tileImageHasError || slideshowImageHasError || projectDetailsHasErrors || (
                isFormValuesSame && isLibraryFormValuesSame
            );
            setSaveDisabled(saveDisabled);
        } else {
            const saveDisabled = projectDetailsHasErrors ? true : isFormValuesSame
            setSaveDisabled(saveDisabled);
        }
    }, [projectDetails, developerMode, libraryDetails, masterApp])

    function areTagsSame(currentValue, defaultValue) {
        if (currentValue.length !== defaultValue.length) {
            return false;
        }
        for (let tagIndex = 0; tagIndex < currentValue.length; tagIndex++) {
            const tag = currentValue[tagIndex];
            const isTagUsedInDefault = defaultValue.some(defaultTag => defaultTag.trim() === tag.trim());
            if (!isTagUsedInDefault) {
                return false;
            }
        }
        return true;
    }

    const [tagField, setTagField] = useState(null);

    const handleEnterInField = useCallback((event) => {
        if (event.keyCode === 13) {
            const inputValue = tagField && tagField._input.value;
            if (inputValue) {
                setProjectDetails(prevValue => {
                    const tags = [...prevValue.tags];
                    const isValueUsed = tags.some(tag => tag.toLowerCase().trim() === inputValue.toLowerCase().trim());
                    if (!isValueUsed) {
                        tags.push(inputValue);
                    }
                    return {
                        ...prevValue,
                        tags
                    }
                })
                tagField.setState({
                    text: ""
                })
            }
        }
    }, [projectDetails, tagField])

    useEffect(() => {
        if (tagField && tagField._input) {
            tagField._input.addEventListener("keydown", handleEnterInField)
        }
    }, [handleEnterInField])

    function handleProjectDetailsChange(e) {
        const { name, value } = e.target;
        setProjectDetails(prevValue => ({
            ...prevValue,
            [name]: value
        }))

        const error = validateField(name, value);
        setProjectDetailErrors(prevValue => ({
            ...prevValue,
            [name]: error
        }));
    }

    function handleDeveloperModeChange(e) {
        setDeveloperMode(e.value);
    }

    function handleMasterAppChange(value) {
        setMasterApp(value);
        setShowMasterAppToogleWarningDialog(false);
    }

    function handleProjectDetailsSwitchChange(e) {
        const { name } = e.target;
        const { value } = e;
        setProjectDetails(prevValue => ({
            ...prevValue,
            [name]: value
        }));
    }

    function handleIconChange(value) {
        handleProjectDetailsChange({ target: { name: "icon", value } });
    }

    function handleLibraryDetailBooleanFieldChange(e) {
        const { value } = e;
        const { name } = e.target;
        setLibraryDetails(prevValue => ({
            ...prevValue,
            [name]: value
        }))
    }

    function handleLibraryDetailMasterAppIdChange(e) {
        const { value } = e;
        const { name } = e.target;
        setLibraryDetails(prevValue => ({
            ...prevValue,
            [name]: value._id
        }))
    }

    function addFileToQueueOfUploadField(event) {
        if (event.newState.length <= 10) {
            handleLibraryDetailFileFieldChange(event);
        }
    }

    function handleLibraryDetailFileFieldChange(event) {
        const { name } = event.target.props;
        const { newState } = event;
        setLibraryDetails(prevValue => ({
            ...prevValue,
            [name]: newState
        }));
    }

    function validateField(name, value) {
        switch (name) {
            case 'name':
                if (value.trim() === "") {
                    return t("errors.name_required");
                }
                if (hasSpecialCharacters(value.trim())) {
                    return t("errors.name_contains_special_characters");
                }
                if (startsWithNumber(value.trim())) {
                    return t("errors.name_starts_with_number");
                }
                if (isProjectNameUsed(value.trim())) {
                    return t("errors.name_already_exists");
                }
                return ""
            default:
                return ""
        }
    }

    function isProjectNameUsed(name) {
        return projects.some(existingProject => (
            existingProject.name.trim().toLowerCase().replace(/[^a-zA-Z0-9]/g, "") === name.trim().toLowerCase().replace(/[^a-zA-Z0-9]/g, "") &&
            existingProject._id !== project._id
        ));
    }

    function hasSpecialCharacters(string) {
        return /[^a-zA-Z0-9 ]/g.test(string);
    }

    function startsWithNumber(string) {
        return /^\d+/g.test(string);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        try {
            setSaving(true);
            const properties = {
                name: projectDetails.name.trim().replace(/\s\s+/g, " "),
                url: projectDetails.name.trim().replace(/[^a-zA-Z0-9\~\_\-\.]/g, ""),
                abbreviation: projectDetails.abbreviation,
                tag: projectDetails.tags.join(','),
                description: projectDetails.description,
                Icon: projectDetails.icon,
                developerMode: developerMode,
                masterApp: masterApp === true,
                allowSyncingSharedProperty: projectDetails.allowSyncingSharedProperty
            };

            if (isLibrary && developerMode) {
                const {
                    tilesImages, slideshowImages, existingTileImages, existingSlideshowImages,
                    ...requiredProperties
                } = libraryDetails;
                // set required properties in the main properties object
                // so it can be included while updating the node
                Object.keys(requiredProperties).forEach(key => properties[key] = requiredProperties[key]);

                if (tilesImages.length > 0) {
                    const uploadedTileImages = await uploadFiles(tilesImages, organization._id, project._id);
                    if (defaultLibraryDetails.existingTileImages.length > 0) {
                        const fileName = defaultLibraryDetails.existingTileImages[0]
                        let path = organization._id + "/" + project._id + "/ProjectImages/" + fileName;
                        DeleteFile(path, fileName)
                    }
                    properties.TilesImages = uploadedTileImages.join(',');
                }
                if (slideshowImages.length > 0) {
                    const uploadedSlideshowImages = await uploadFiles(slideshowImages, organization._id, project._id);
                    properties.SlideshowImages = [
                        ...defaultLibraryDetails.existingSlideshowImages, ...uploadedSlideshowImages
                    ].join(',');
                }
            }
            await updateProject(project._id, properties);

            if (properties.name !== defaultProjectDetails.name) {
                const [changeOnRuntimeStatus, changeOnRuntimeMessage] = await updateProjectLabelAndUrlOnRuntime(
                    organization._id, properties.url, properties.name, msalContext, project.instances
                )
                if (changeOnRuntimeStatus === "error") {
                    console.error(changeOnRuntimeMessage);
                    alert("Something went wrong while making changes. Please contact else you may lose access to your " + changeOnRuntimeMessage.instance + " instance");
                } else {
                    if (isAccessedInIframe) {
                        postMessageToRuntime({
                            reload: true,
                            organizationUrl: organization.orgUrl,
                            projectUrl: properties.url
                        })
                    } else {
                        redirectToNewUrl();
                    }
                }
            }

            if (
                !isAccessedInIframe &&
                developerMode !== defaultProjectDetails.developerMode
            ) {
                redirectToNewUrl();
            }

            // reset default project details
            const newProjectDetails = {
                ...projectDetails,
                url: properties.url,
                developerMode,
                masterApp,
                allowSyncingSharedProperty: projectDetails.allowSyncingSharedProperty
            }
            setDefaultProjectDetails(newProjectDetails);
            setProjectDetails({ ...newProjectDetails });
            if (isLibrary && developerMode) {
                const { tilesImages, slideshowImages, ...restLibraryDetails } = libraryDetails;
                const clonedRestLibraryDetails = structuredClone(restLibraryDetails);
                clonedRestLibraryDetails.existingTileImages = properties.TilesImages ? properties.TilesImages.split(',') : defaultLibraryDetails.existingTileImages;
                clonedRestLibraryDetails.existingSlideshowImages = properties.SlideshowImages ? properties.SlideshowImages.split(',') : defaultLibraryDetails.existingSlideshowImages;
                setDefaultLibraryDetails(clonedRestLibraryDetails);
                setLibraryDetails(prevState => ({
                    ...prevState,
                    tilesImages: [],
                    slideshowImages: []
                }));
            }
        } catch (e) {
            setShowNetworkError(true);
        } finally {
            setSaveDisabled(true);
            setSaving(false);
        }
    }

    function redirectToNewUrl() {
        window.location.href = `/${organization.orgName}/${projectDetails.name}/Setting/General`;
    }

    function toggleDeleteDialog() {
        setShowDeleteDialog(!showDeleteDialog);
    }

    function toggleMasterAppToogleWarningDialog() {
        setShowMasterAppToogleWarningDialog(!showMasterAppToogleWarningDialog);
    }

    function toggleCloneAppDialog() {
        setShowCloneAppDialog(!showCloneAppDialog);
    }

    function toggleUpgradeDialog() {
        setShowUpgradeDialog(!showUpgradeDialog);
    }

    async function cloneApp() {
        try {
            setSaving(true);
            debugger;
            const response = await callApiWithAuthorization(`/api/v0/clone-in-same-organization/${organization._id}/${project._id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    organizationId: organization._id,
                    projectId: project._id
                })
            });

            const result = await response.json();
            console.log('Clone response:', result);
            
            // Check if the response indicates upgrade is needed
            if (result.status === "failed" && result.error === "Max apps allowed limit reached" && result.type === "upgrade") {
                // Close the clone dialog
                setShowCloneAppDialog(false);
                // Show upgrade dialog
                setShowUpgradeDialog(true);
            } else if (result.status === "success") {
                // Close the dialog
                setShowCloneAppDialog(false);
                // Show success message
                alert('App cloned successfully!');
            } else {
                // Handle other errors
                console.error('Clone failed:', result);
                setShowNetworkError(true);
            }
        } catch (error) {
            console.error('Error cloning app:', error);
            setShowNetworkError(true);
        } finally {
            setSaving(false);
        }
    }

    async function disableProject() {
        const [disableStatus, disableMessage] = await disableAllInstancesOfApplicationOnRuntime(
            msalContext, organization._id, project.instances, defaultProjectDetails.name
        )

        if (disableStatus === "error") {
            setShowNetworkError(true);
            toggleDeleteDialog();
            return;
        }

        await updateProject(project._id, {
            isActive: false,
            deletedBy: user._id,
            deletedOn: new Date()
        });

        if (isAccessedInIframe) {
            postMessageToRuntime({
                reload: true,
                organizationUrl: organization.orgUrl
            })
        } else {
            navigate(`/${organization.orgName}`);
        }
    }

    function toggleRemoveFileDialog() {
        setShowRemoveFileDialog(!showRemoveFileDialog);
    }

    function handleRemoveFileClick(fileName) {
        setFileToRemove(fileName);
        toggleRemoveFileDialog();
    }

    async function removeFile() {
        const arrayNameToRemoveFrom = defaultLibraryDetails.existingTileImages[0] === fileToRemove ? "existingTileImages" : "existingSlideshowImages";
        const url = organization._id + "/" + project._id + "/ProjectImages/" + fileToRemove;
        DeleteFile(url);
        const arrayWithRemovedValue = defaultLibraryDetails[arrayNameToRemoveFrom].filter(value => value !== fileToRemove);
        setDefaultLibraryDetails(prevValue => ({
            ...prevValue,
            [arrayNameToRemoveFrom]: arrayWithRemovedValue
        }));
        const propertyName = arrayNameToRemoveFrom === "existingSlideshowImages" ? "SlideshowImages" : "TilesImages";
        await updateProject(project._id, {
            [propertyName]: arrayWithRemovedValue.join(',')
        })
        closeDeleteFileDialog();
    }

    function closeDeleteFileDialog() {
        setFileToRemove("");
        toggleRemoveFileDialog();
    }

    function handleTagBlur(e, onChange) {
        const field = tagField
        const inputValue = field && field._input.value;
        if (inputValue) {
            const tags = [...projectDetails.tags];
            const isValueUsed = tags.some(tag => tag.toLowerCase().trim() === inputValue.toLowerCase().trim());
            if (!isValueUsed) {
                tags.push(inputValue);
            }
            field.setState(prevState => ({
                ...prevState,
                text: ""
            }))
            onChange({
                target: {
                    name: e.target.name,
                    value: tags
                }
            })
        }
    }

    if (projectsLoading) {
        return (
            <div className='container-fluid h-100'>
                <div className='row h-100'>
                    <div className='col-12 h-100 d-flex align-items-center justify-content-center'>
                        <Loader />
                    </div>
                </div>
            </div>
        )
    }

    if (projectsLoadingError) {
        return (
            <div className='container-fluid h-100'>
                <div className='row h-100'>
                    <div className='col-12 h-100 d-flex align-items-center justify-content-center flex-column text-danger'>
                        <h3>
                            {t("errors.loading_error_warning1")}
                        </h3>
                        <p>
                            {t("errors.loading_error_warning2")}
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <PageContainer>
            {
                showRemoveFileDialog &&
                <RemoveDialog
                    Title={t("delete_file_dialog.title")}
                    message={t("delete_file_dialog.warning", { fileName: fileToRemove })}
                    deleteButtonText={t("delete_file_dialog.buttons.delete")}
                    cancelButtonText={t("delete_file_dialog.buttons.cancel")}
                    onDelete={removeFile}
                    toggleDeleteDialog={closeDeleteFileDialog} />
            }

            {
                showMasterAppToogleWarningDialog &&
                <SaveDialog
                    Title={t("master_app_warning_dialog.title")}
                    message={t("master_app_warning_dialog.warning")}
                    message2={t("master_app_warning_dialog.warning2")}
                    onSave={() => handleMasterAppChange(true)}
                    onCancel={toggleMasterAppToogleWarningDialog}
                    toggleSaveDialog={toggleMasterAppToogleWarningDialog}
                    SaveButtonText={t("master_app_warning_dialog.buttons.confirm")}
                    cancelButtonText={t("master_app_warning_dialog.buttons.cancel")}
                />
            }

            {
                showCloneAppDialog &&
                <SaveDialog
                    Title={t("clone_app.title")}
                    message={t("clone_app.warning")}
                    onSave={cloneApp}
                    onCancel={toggleCloneAppDialog}
                    toggleSaveDialog={toggleCloneAppDialog}
                    SaveButtonText={t("clone_app.buttons.yes_clone")}
                    cancelButtonText={t("clone_app.buttons.cancel")}
                />
            }

            {
                showUpgradeDialog &&
                <Upgarde
                    organization={organization}
                    Headline="Need to build <br/>more apps?"
                    toggleDialog={toggleUpgradeDialog}
                />
            }

            {!isAccessedInIframe &&
                <Header
                    header={t("header_title")}
                    headerIcon={
                        <i
                            style={{ background: defaultProjectDetails.bgColor, color: "#fff" }}
                            className={`${defaultProjectDetails.icon} app-icon rounded app-icon p-2 me-2`} />
                    }>
                </Header>
            }

            {showNetworkError &&
                <span className='m-2 alert alert-danger col-md-6'>
                    {t("errors.network_error_warning")}
                </span>

            }

            <Scrollable>
                <div className={`container-fluid ${isAccessedInIframe ? 'onlyIframe' : ''}`}>
                    <div className='row gx-5'>
                        <div className='col-6'>
                            <div className='form-group'>
                                <label className='form-label' required>
                                    {t("fields.name")}
                                    {
                                        projectDetailsErrors.name &&
                                        <ErrorMessage
                                            valid={false}
                                            error={projectDetailsErrors.name}
                                        />
                                    }
                                </label>
                                <input
                                    type="text"
                                    className='form-control name-text'
                                    name="name"
                                    value={projectDetails.name}
                                    onChange={handleProjectDetailsChange} />
                            </div>
                            <div className='form-group mt-2'>
                                <label className='form-label' required>
                                    {t("fields.url")}
                                </label>
                                <div className='font-size-1 fontUrl'>
                                    {process.env.REACT_APP_RUNTIME_URL}/{organization.orgUrl}/
                                    <span className='text-primary'>{defaultProjectDetails.url}</span>
                                </div>
                                <div className='disabled-input-wrapper mt-2'>
                                    <input
                                        type="text"
                                        placeholder='Enter value here..'
                                        className="form-control name-text"
                                        defaultValue={projectDetails.url}
                                        disabled />
                                </div>
                            </div>
                            {
                                isInternalUser && defaultProjectDetails.developerMode &&
                                <>
                                    <div className='form-group mt-2'>
                                        <label className='form-label'>
                                            {t("fields.description")}
                                        </label>
                                        <textarea
                                            rows={4}
                                            className='form-control'
                                            name='description'
                                            onChange={handleProjectDetailsChange}
                                            value={projectDetails.description} />
                                    </div>
                                    <div className='form-group mt-2'>
                                        <label className='form-label'>
                                            {t("fields.tags")}
                                        </label>
                                        <div className="custom-tag">
                                            <MultiSelect
                                                allowCustom
                                                name='tags'
                                                onChange={handleProjectDetailsChange}
                                                onBlur={(e) => handleTagBlur(e, handleProjectDetailsChange)}
                                                value={projectDetails.tags}
                                                data={[]}
                                                opened={false}
                                                ref={setTagField} />
                                        </div>
                                    </div>
                                    <div className='form-group mt-2'>
                                        <label className='form-label'>
                                            {t("fields.abbreviation")}
                                        </label>
                                        <input
                                            className='form-control'
                                            name='abbreviation'
                                            onChange={handleProjectDetailsChange}
                                            value={projectDetails.abbreviation} />
                                    </div>
                                    <div className='form-group mt-2'>
                                        <label className='form-label'>
                                            {t("fields.icon")}
                                        </label>
                                        <IconPicker
                                            selected={projectDetails.icon}
                                            onChange={handleIconChange} />
                                    </div>
                                </>
                            }
                            {
                                isInternalUser && organization.internalMode &&
                                <div className='form-group mt-2'>
                                    <label className='form-label' required>
                                        {t("fields.advanced_mode")}
                                    </label>
                                    <div>
                                        <Switch
                                            name='developerMode'
                                            onChange={handleDeveloperModeChange}
                                            value={developerMode}
                                            checked={developerMode} />
                                    </div>
                                </div>
                            }
                            {
                                !organization.isTenantOrganization && subscription.showMasterApp &&
                                <div className='form-group mt-2'>
                                    <label className='form-label' required>
                                        {t("fields.master_app")}
                                    </label>
                                    <div>
                                        <Switch
                                            name='masterApp'
                                            disabled={defaultProjectDetails.masterApp}
                                            onChange={(e) => { e.value ? toggleMasterAppToogleWarningDialog() : handleMasterAppChange(false) }}
                                            value={masterApp}
                                            checked={masterApp} />
                                    </div>
                                </div>
                            }
                            <div className='form-group mt-2'>
                                <label className='form-label'>
                                    {t("fields.allow_syncing_shared_property")}
                                </label>
                                <div>
                                    <Switch
                                        name='allowSyncingSharedProperty'
                                        onChange={handleProjectDetailsSwitchChange}
                                        value={projectDetails.allowSyncingSharedProperty}
                                        checked={projectDetails.allowSyncingSharedProperty} />
                                </div>
                            </div>
                            <div className='mt-4 d-flex align-items-center'>
                                <button
                                    type='submit'
                                    onClick={handleSubmit}
                                    className='btn btn-primary'
                                    disabled={saveDisabled}
                                >
                                    {t("buttons.save")}
                                </button>
                                {
                                    saving &&
                                    <span>
                                        &nbsp;
                                        &nbsp;
                                        <OverlayLoader />
                                        <InnerLoader borderColor="green" />
                                    </span>
                                }
                            </div>
                        </div>
                        {
                            isLibrary &&
                            isInternalUser && defaultProjectDetails.developerMode &&
                            <div className='col-5 font-size-1'>
                                <div className='form-group'>
                                    <label className='form-label'>
                                        {t("fields.choose_source_enviroment")}
                                    </label>
                                    <div>
                                        <DropDownList
                                            data={project.instances}
                                            name='masterAppId'
                                            textField='instanceName'
                                            valueField='_id'
                                            value={project.instances.find(item => item._id === libraryDetails.masterAppId)}
                                            onChange={handleLibraryDetailMasterAppIdChange}
                                        />
                                    </div>
                                </div>
                                <div className='form-group'>
                                    <label className='form-label'>
                                        {t("fields.publish_to_app_library")}
                                    </label>
                                    <div>
                                        <Switch
                                            name="isPublished"
                                            checked={libraryDetails.isPublished}
                                            value={libraryDetails.isPublished}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                    </div>
                                </div>
                                <div className='form-group'>
                                    <label className='form-label'>
                                        {t("fields.super_app")}
                                    </label>
                                    <div>
                                        <Switch
                                            name="isSuperApp"
                                            checked={libraryDetails.isSuperApp}
                                            value={libraryDetails.isSuperApp}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                    </div>
                                </div>
                                <div className='form-group'>
                                    <label className='form-label'>
                                        {t("fields.featured_app")}
                                    </label>
                                    <div>
                                        <Switch
                                            name='isFeaturedApp'
                                            checked={libraryDetails.isFeaturedApp}
                                            value={libraryDetails.isFeaturedApp}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                    </div>
                                </div>
                                <div className='form-group'>
                                    <label className='form-label'>
                                        {t("fields.features")}
                                    </label>
                                    <div>
                                        <Checkbox
                                            className='me-3'
                                            label={t("fields.features_fields.table")}
                                            labelPlacement="after"
                                            name='hasTableFeature'
                                            value={libraryDetails.hasTableFeature}
                                            checked={libraryDetails.hasTableFeature}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                        <Checkbox
                                            className='me-3'
                                            label={t("fields.features_fields.card")}
                                            labelPlacement="after"
                                            name="hasCardsFeature"
                                            value={libraryDetails.hasCardsFeature}
                                            checked={libraryDetails.hasCardsFeature}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                        <Checkbox
                                            className='me-3'
                                            label={t("fields.features_fields.calendar")}
                                            labelPlacement="after"
                                            name="hasCalendarFeature"
                                            value={libraryDetails.hasCalendarFeature}
                                            checked={libraryDetails.hasCalendarFeature}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                        <Checkbox
                                            className='me-3'
                                            label={t("fields.features_fields.split_view")}
                                            labelPlacement="after"
                                            name="hasSplitViewFeature"
                                            value={libraryDetails.hasSplitViewFeature}
                                            checked={libraryDetails.hasSplitViewFeature}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                    </div>
                                    <div>
                                        <Checkbox
                                            className='me-3'
                                            label={t("fields.features_fields.list")}
                                            labelPlacement="after"
                                            name="hasListsFeature"
                                            value={libraryDetails.hasListsFeature}
                                            checked={libraryDetails.hasListsFeature}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                        <Checkbox
                                            className='me-3'
                                            label={t("fields.features_fields.chart")}
                                            labelPlacement="after"
                                            name="hasChartsFeature"
                                            value={libraryDetails.hasChartsFeature}
                                            checked={libraryDetails.hasChartsFeature}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                        <Checkbox
                                            className='me-3'
                                            label={t("fields.features_fields.reporting")}
                                            labelPlacement="after"
                                            name="hasReportingFeature"
                                            value={libraryDetails.hasReportingFeature}
                                            checked={libraryDetails.hasReportingFeature}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                        <Checkbox
                                            className='me-3'
                                            label={t("fields.features_fields.dashboard")}
                                            labelPlacement="after"
                                            name="hasDashboardFeature"
                                            value={libraryDetails.hasDashboardFeature}
                                            checked={libraryDetails.hasDashboardFeature}
                                            onChange={handleLibraryDetailBooleanFieldChange} />
                                    </div>
                                </div>
                                <div className="form-group mt-3">
                                    <small className="form-label">
                                        {t("fields.tiles_images")}
                                    </small>
                                    <Upload
                                        showActionButtons={false}
                                        // disabled={this.props.projectRole !== "Project Administrator"}
                                        className="create-project-upload"
                                        multiple={false}
                                        autoUpload={false}
                                        name={"tilesImages"}
                                        onAdd={addFileToQueueOfUploadField}
                                        onRemove={handleLibraryDetailFileFieldChange}
                                        files={libraryDetails.tilesImages}
                                        restrictions={{
                                            allowedExtensions: [
                                                ".jpg",
                                                ".png",
                                            ],
                                            maxFileSize: 4000000,
                                        }}
                                    />
                                    {
                                        defaultLibraryDetails.existingTileImages &&
                                        defaultLibraryDetails.existingTileImages.length > 0 &&
                                        <ProjectSettingFileList
                                            organizationId={organization._id}
                                            projectId={project._id}
                                            files={defaultLibraryDetails.existingTileImages}
                                            onRemoveClick={handleRemoveFileClick} />
                                    }
                                </div>
                                <div className="form-group mt-3">
                                    <small className="form-label">
                                        {t("fields.slideshow_images")}
                                    </small>
                                    <Upload
                                        showActionButtons={false}
                                        // disabled={this.props.projectRole !== "Project Administrator"}
                                        className="create-project-upload"
                                        autoUpload={false}
                                        name="slideshowImages"
                                        onAdd={addFileToQueueOfUploadField}
                                        onRemove={handleLibraryDetailFileFieldChange}
                                        files={libraryDetails.slideshowImages}
                                        restrictions={{
                                            allowedExtensions: [
                                                ".jpg",
                                                ".png",
                                            ],
                                            maxFileSize: 4000000,
                                        }}
                                    />
                                    {
                                        defaultLibraryDetails.existingSlideshowImages &&
                                        defaultLibraryDetails.existingSlideshowImages.length > 0 &&
                                        <ProjectSettingFileList
                                            organizationId={organization._id}
                                            projectId={project._id}
                                            files={defaultLibraryDetails.existingSlideshowImages}
                                            onRemoveClick={handleRemoveFileClick} />
                                    }
                                </div>
                            </div>
                        }
                    </div>
                    <div className='row mt-4'></div>
                    <div className='row border-top'></div>
                    {!organization.isTenantOrganization &&
                        <div className='row mt-4'>
                            {
                                showDeleteDialog &&
                                <DeleteProjectDialog
                                    organization={organization}
                                    toggleDialog={toggleDeleteDialog}
                                    projectName={defaultProjectDetails.name}
                                    onDelete={disableProject} />
                            }
                            {!tenantsLoading && tenants.length === 0 &&
                                <div className='col-4'>
                                    <h5>
                                        {t("delete_app.title")}
                                    </h5>
                                    <button
                                        disabled={tenants.length > 0}
                                        className='btn btn-danger px-4'
                                        onClick={toggleDeleteDialog}>
                                        <i className='fa-solid fa-trash-can me-2' />
                                        {t("delete_app.buttons.delete")}
                                    </button>
                                    <div className="py-2 fontUrl">
                                        <i className="fa-solid fa-triangle-exclamation mx-1" style={{ color: 'coral' }}></i>
                                        {t("delete_app.warning")}
                                    </div>
                                </div>
                            }
                            <div className='col-12 mb-2'>
                                <h5>
                                    {t("clone_app.title")}
                                </h5>
                                <button
                                    className='btn btn-primary px-4 me-3'
                                    onClick={toggleCloneAppDialog}>
                                    <i className='fa-solid fa-copy me-2' />
                                    {t("clone_app.buttons.clone")}
                                </button>
                            </div>
                        </div>
                    }
                    <div className='text-muted py-4'>{t("version_number")}: {process.env.REACT_APP_BUILD_VERSION}</div>
                </div>
            </Scrollable>
        </PageContainer>
    )
}

ProjectSetting.defaultProps = {
    project: {
        tags: ""
    }
}
